import React, { useCallback, useRef, useMemo } from 'react';
import { useTranslation } from '../../../../localization/useTranslation';
import Tl from '../../../../localization/LocalizationKeys';
import _ from 'lodash';
import ModelType from '../../../Model/ModelType';
import { PRIMITIVE_SCHEMAS } from '../../../../utils/dtdl';
import ContentTable from '../utils/ContentTable';
import { dtdlNameError } from '../utils/model';

const columns = [
  {
    label: Tl.model.name,
    path: 'name',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.displayName,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.description,
    path: 'description',
    searchItem: true
  },
  {
    label: Tl.model.comment,
    path: 'comment',
    searchItem: true
  },
  {
    label: Tl.model.command.request.payload,
    path: 'request',
    searchItem: true,
    render: (val) => JSON.stringify(val)
  },
  {
    label: Tl.model.command.response.payload,
    path: 'response',
    searchItem: true,
    render: (val) => JSON.stringify(val)
  }
];


export const CommandTab = ({ commands, onCommandsUpdate, currentNames, modelDraft }) => {
  const T = useTranslation();
  const currentlyEditingItem = useRef({});

  const nameHasError = useCallback((name) => {
    return dtdlNameError(name, _.reject(currentNames, n => currentlyEditingItem.current.name === n));
  }, [currentNames]);

  const commandsModel = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: T.model.name,
        path: 'name',
        isRequired: true,
        isReadOnly: modelDraft === false,
        hasError: nameHasError
      },
      {
        type: ModelType.TEXT,
        label: T.model.displayName,
        path: 'displayName',
        isRequired: true,
        isReadOnly: modelDraft === false,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        label: T.model.description,
        path: 'description',
        isRequired: false,
        isReadOnly: modelDraft === false
      },
      {
        type: ModelType.TEXT,
        label: T.model.comment,
        path: 'comment',
        isReadOnly: modelDraft === false
      },
      {
        type: ModelType.SUB_DIALOG,
        label: T.model.command.request.payload,
        path: 'request',
        subDialogTitle: 'Request Payload',
        subDialogModel: [
          {
            type: ModelType.TEXT,
            label: 'name',
            path: 'request.name',
            isRequired: true,
            isReadOnly: modelDraft === false,
            hasError: _.isEmpty
          },
          {
            type: ModelType.TEXT,
            label: T.model.displayName,
            path: 'request.displayName',
            isRequired: true,
            isReadOnly: modelDraft === false,
            hasError: _.isEmpty
          },
          {
            type: ModelType.OPTIONSV2,
            label: T.model.schema,
            path: 'request.schema',
            isRequired: true,
            disabled: modelDraft === false,
            hasError: _.isEmpty,
            options: _.map(PRIMITIVE_SCHEMAS, (value) => ({ value, text: value })),
            properties: {
              isTypeahead: true
            }
          },
          {
            type: ModelType.TEXT,
            label: T.model.description,
            path: 'request.description',
            isReadOnly: modelDraft === false
          },
          {
            type: ModelType.TEXT,
            label: T.model.comment,
            path: 'request.comment',
            isReadOnly: modelDraft === false
          }
        ]
      },
      {
        type: ModelType.SUB_DIALOG,
        label: T.model.command.response.payload,
        path: 'response',
        subDialogTitle: 'Response Payload',
        subDialogModel: [
          {
            type: ModelType.TEXT,
            label: 'name',
            path: 'response.name',
            isRequired: true,
            isReadOnly: modelDraft === false,
            hasError: _.isEmpty
          },
          {
            type: ModelType.TEXT,
            label: T.model.displayName,
            path: 'response.displayName',
            isRequired: true,
            isReadOnly: modelDraft === false,
            hasError: _.isEmpty
          },
          {
            type: ModelType.OPTIONSV2,
            label: T.model.schema,
            path: 'response.schema',
            isRequired: true,
            disabled: modelDraft === false,
            hasError: _.isEmpty,
            options: _.map(PRIMITIVE_SCHEMAS, (value) => ({ value, text: value })),
            properties: {
              isTypeahead: true
            }
          },
          {
            type: ModelType.TEXT,
            label: T.model.description,
            isReadOnly: modelDraft === false,
            path: 'response.description'
          },
          {
            type: ModelType.TEXT,
            label: T.model.comment,
            isReadOnly: modelDraft === false,
            path: 'response.comment'
          }
        ]
      }
    ];
  }, [T.model.command.request.payload, T.model.command.response.payload, T.model.comment, T.model.description, T.model.displayName, T.model.name, T.model.schema, modelDraft, nameHasError]);

  const onSave = useCallback((content) => {
    onCommandsUpdate(content);
  }, [onCommandsUpdate]);

  return (
    <>
      <ContentTable
        title={T.model.tab.commands}
        columns={columns}
        data={commands}
        dialogModel={commandsModel}
        updatedContent={() => {}}
        onSave={onSave}
        currentlyEditing={item => {
          currentlyEditingItem.current = item;
        }}
        modelDraft={modelDraft}
      />

    </>
  );
};
